import React from 'react';
import { useKioskDeviceSession } from '@/hooks/kiosk';
import { Button } from '@/components/ui/button';
import { LogOut, Loader2, Building2, Settings } from 'lucide-react';
import { toast } from '@/hooks/ui/use-toast';
import { KioskPairingScreen } from './KioskPairingScreen';

interface KioskAuthGateProps {
  children: React.ReactNode;
}

/**
 * Authentication gate for kiosk mode using device pairing
 * Flow: Pairing Screen (if unpaired) -> Show Content (if paired)
 */
export function KioskAuthGate({ children }: KioskAuthGateProps) {
  const {
    isPaired,
    isValidating,
    deviceStatus,
    businessName,
    deviceName,
    allowedModes,
    pairingCode,
    pairingExpiresAt,
    pairingError,
    isPairingCodeLoading,
    requestPairingCode,
    unpair,
  } = useKioskDeviceSession();

  // Show loading while validating existing session
  if (isValidating) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">Connecting...</p>
        </div>
      </div>
    );
  }

  // Show pairing screen if not paired
  if (!isPaired) {
    return (
      <KioskPairingScreen
        pairingCode={pairingCode}
        expiresAt={pairingExpiresAt}
        error={pairingError}
        isLoading={isPairingCodeLoading}
        onRequestNewCode={requestPairingCode}
      />
    );
  }

  // Device was revoked
  if (deviceStatus === 'revoked') {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
            <Settings className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Device Disconnected</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            This device has been removed from the business. Please pair it again to continue.
          </p>
          <Button onClick={requestPairingCode}>
            Pair Again
          </Button>
        </div>
      </div>
    );
  }

  // Device is inactive
  if (deviceStatus === 'inactive') {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center mx-auto mb-4">
            <Settings className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Kiosk Disabled</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            This kiosk has been temporarily disabled by an administrator. Contact your manager to re-enable it.
          </p>
          <Button variant="outline" onClick={unpair}>
            <LogOut className="w-4 h-4 mr-2" />
            Unpair Device
          </Button>
        </div>
      </div>
    );
  }

  // Show content with header
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Business info bar */}
      <div className="flex-shrink-0 h-12 flex items-center justify-between px-6 bg-gray-100 dark:bg-muted border-b border-gray-200 dark:border-border">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-gray-500" />
          <span className="text-gray-900 dark:text-gray-100 font-medium">
            {businessName || 'Connected'}
          </span>
          {deviceName && (
            <span className="text-gray-500 dark:text-gray-400 text-sm">
              • {deviceName}
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            unpair();
            toast.success('Device unpaired');
          }}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Unpair
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {children}
      </div>
    </div>
  );
}
